import {useMutation} from '@apollo/react-hooks';

import {safelyGetGraphQLErrorMessage} from 'nutshell-core/utils/graphql-errors';

import type {
    ReassignThread as ReassignThreadMutation,
    ReassignThreadVariables as ReassignThreadMutationVariables,
    ThreadReassignInput,
} from 'nutshell-graphql-types';

import {useGetSessionUser} from '../../../core/use-get-session-user';

import INBOX_THREAD_FRAGMENT from '../fragments/inbox-thread-fragment.graphql';
import REASSIGN_THREAD from '../mutations/reassign-thread.graphql';

export const useReassignThread = (): {
    reassignThread: (
        input: ThreadReassignInput & {
            assigneeName: string;
            assigneeInitials: string;
            assigneeAvatarUrl: string;
        }
    ) => Promise<any>;
    isLoading: boolean;
    error: string | null | undefined;
} => {
    const [reassignThread, {loading, error}] = useMutation<
        ReassignThreadMutation,
        ReassignThreadMutationVariables
    >(REASSIGN_THREAD);

    const {user} = useGetSessionUser();

    return {
        reassignThread: (
            input: ThreadReassignInput & {
                assigneeName: string;
                assigneeInitials: string;
                assigneeAvatarUrl: string;
            }
        ) => {
            const {assigneeName, assigneeAvatarUrl, assigneeInitials, ...restInput} = input;
            const isAssignedToCurrentUser = user && user.id === input.assigneeId;

            return reassignThread({
                variables: {input: {...restInput}},
                refetchQueries: ['GetInboxSidebarCounts'],
                // only do optimistic response if the user is not being unassigned to avoid flashing a "Unassigned" user
                optimisticResponse: restInput.assigneeId
                    ? {
                          // @ts-expect-error
                          __typename: 'Mutation',
                          threadReassign: {
                              __typename: 'ThreadReassignPayload',
                              thread: {
                                  __typename: 'Thread',
                                  id: restInput.threadId,
                              },
                              owner: {
                                  __typename: 'User',
                                  id: restInput.assigneeId,
                                  name: assigneeName,
                                  avatarUrl: assigneeAvatarUrl,
                                  initials: assigneeInitials,
                              },
                              isAssignedToCurrentUser,
                          },
                      }
                    : undefined,
                update: (cache, {data}) => {
                    const updatedOwner = data && data.threadReassign && data.threadReassign.owner;

                    const cacheResult = cache.readFragment({
                        id: restInput.threadId,
                        fragmentName: 'InboxThreadFragment',
                        fragment: INBOX_THREAD_FRAGMENT,
                    });

                    if (cacheResult && updatedOwner) {
                        cache.writeFragment({
                            id: restInput.threadId,
                            fragmentName: 'InboxThreadFragment',
                            fragment: INBOX_THREAD_FRAGMENT,
                            data: {
                                ...cacheResult,
                                owner: updatedOwner,
                                isAssignedToCurrentUser,
                            },
                        });
                    }
                },
            });
        },
        isLoading: loading,
        // @ts-expect-error
        error: error && safelyGetGraphQLErrorMessage(error),
    };
};
